import { useEffect, useRef, useState } from "react";
import { BottomSheet } from "../ui/BottomSheet";
import { ExpenseImport } from "./ExpenseImport";
import {
	expenseJpy,
	expensesCsv,
	jpyToKrw,
	settleExpenses,
	tripParty,
	type TripExpense,
	type TripWorkspace,
} from "@japangolmok/core";
import type { PersistTrip } from "./TripExperience";
import { CopyText, SendPayload } from "./ShareSheet";
import { downloadTripFile } from "./TripTransfer";
import { track } from "../analytics/events";
import { isAppsInTossChannel } from "../api-url";
const categories = {
	transport: "교통",
	food: "식사",
	stay: "숙박",
	shopping: "쇼핑",
	experience: "경험",
	other: "기타",
} as const;
export function MoneyPanel({
	trip,
	persist,
	quick = false,
	date,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
	quick?: boolean;
	date?: string;
}) {
	const [editing, setEditing] = useState<TripExpense | null>(null),
		[error, setError] = useState(""),
		[notice, setNotice] = useState(""),
		[currency, setCurrency] = useState<"JPY" | "KRW">("JPY"),
		[paidBy, setPaidBy] = useState(""),
		[split, setSplit] = useState<string[] | null>(null),
		[send, setSend] = useState(false),
		[showForm, setShowForm] = useState(false),
		[saving, setSaving] = useState(false);
	const dirty = useRef(false);
	const saveLock = useRef(false);
	const party = tripParty(trip),
		settlement = settleExpenses(trip),
		sum = trip.expenses.reduce(
			(n, e) => n + (expenseJpy(e, trip.krwPer100Jpy) ?? 0),
			0,
		),
		unconverted = trip.expenses
			.filter((e) => e.currency === "KRW" && !trip.krwPer100Jpy)
			.reduce((n, e) => n + (e.amountKrw ?? 0), 0),
		selectedDate = date ?? trip.startDate,
		todaySpent = trip.expenses
			.filter((e) => e.occurredOn === selectedDate)
			.reduce((n, e) => n + (expenseJpy(e, trip.krwPer100Jpy) ?? 0), 0);
	useEffect(() => {
		if (!quick) track("settlement_viewed");
	}, [quick]);
	const name = (id: string) => party.find((p) => p.id === id)?.nickname ?? "나";

	const closeForm = () => {
		if (saveLock.current) return;
		if (dirty.current && !window.confirm("입력한 내용을 버리고 닫을까요?"))
			return;
		dirty.current = false;
		setEditing(null);
		setShowForm(false);
		setError("");
	};
	const expenseForm = (
		<form
			key={editing?.id ?? "new"}
			noValidate
			className="compact-form"
			onChange={() => {
				dirty.current = true;
			}}
			onSubmit={async (e) => {
				e.preventDefault();
				if (saveLock.current) return;
				const form = e.currentTarget,
					d = new FormData(form),
					amount = Number(d.get("amount")),
					day = String(d.get("date") ?? selectedDate);
				if (!Number.isInteger(amount) || amount < 1 || amount > 1000000000) {
					setError("금액을 1 이상 정수로 적어 주세요.");
					return;
				}
				if (!trip.days.some((x) => x.date === day)) {
					setError("여행 안의 날짜를 골라 주세요.");
					return;
				}
				const payer = paidBy || party[0]!.id,
					participants = split ?? party.map((p) => p.id);
				if (!participants.length) {
					setError("나눌 사람을 한 명 이상 골라 주세요.");
					return;
				}
				const expense: TripExpense = {
					id: editing?.id ?? crypto.randomUUID(),
					title:
						String(d.get("title") ?? "현지 지출")
							.trim()
							.slice(0, 80) || "현지 지출",
					category: String(d.get("category")) as TripExpense["category"],
					amountJpy: currency === "JPY" ? amount : 1,
					currency,
					amountKrw: currency === "KRW" ? amount : undefined,
					paidBy: payer,
					splitWith: participants,
					occurredOn: day,
				};
				saveLock.current = true;
				setSaving(true);
				try {
					const ok = await persist((t) => {
						const index = t.expenses.findIndex((x) => x.id === expense.id);
						if (index >= 0) t.expenses[index] = expense;
						else t.expenses.push(expense);
					});
					if (ok !== false) {
						setEditing(null);
						dirty.current = false;
						form.reset();
						setError("");
						setNotice("지출을 기록했어요.");
						track("budget_expense_added");
						if (!quick) {
							if (history.state?.sheet) history.back();
							else setShowForm(false);
						}
					} else setError("저장하지 못했어요. 입력한 내용은 그대로예요.");
				} catch {
					setError("저장하지 못했어요. 입력한 내용은 그대로예요.");
				} finally {
					saveLock.current = false;
					setSaving(false);
				}
			}}
		>
			<label>
				금액 · {quick ? "엔" : currency === "JPY" ? "엔" : "원"}
				<input
					name="amount"
					inputMode="numeric"
					type="number"
					step="1"
					defaultValue={
						editing
							? editing.currency === "KRW"
								? editing.amountKrw
								: editing.amountJpy
							: ""
					}
				/>
			</label>
			{party.length > 1 && !quick && (
				<>
					<label>
						누가 냈나요
						<select
							value={paidBy || party[0]!.id}
							onChange={(e) => setPaidBy(e.target.value)}
						>
							{party.map((p) => (
								<option key={p.id} value={p.id}>
									{p.nickname}
								</option>
							))}
						</select>
					</label>
				</>
			)}
			<button className="button primary" type="submit" disabled={saving}>
				{saving ? "저장 중" : editing ? "지출 고치기" : "지출 저장"}
			</button>
			<details open={!!editing}>
				<summary>날짜·내용·통화와 나눌 사람</summary>
				{!quick && (
					<>
						<label>
							날짜
							<input
								name="date"
								type="date"
								defaultValue={editing?.occurredOn ?? selectedDate}
							/>
						</label>
						<label>
							내용
							<input
								name="title"
								maxLength={80}
								defaultValue={editing?.title ?? ""}
							/>
						</label>
						<label>
							통화
							<select
								value={currency}
								onChange={(e) => setCurrency(e.target.value as typeof currency)}
							>
								<option value="JPY">엔</option>
								<option value="KRW">원 · 카드 결제액</option>
							</select>
						</label>
					</>
				)}
				<label>
					분류
					<select name="category" defaultValue={editing?.category ?? "food"}>
						{Object.entries(categories).map(([k, v]) => (
							<option key={k} value={k}>
								{v}
							</option>
						))}
					</select>
				</label>
				{party.length > 1 && !quick && (
					<fieldset>
						<legend>누구 몫인가요</legend>
						{party.map((p) => (
							<label key={p.id}>
								<input
									type="checkbox"
									checked={(split ?? party.map((x) => x.id)).includes(p.id)}
									onChange={(e) =>
										setSplit(
											e.target.checked
												? [...(split ?? party.map((x) => x.id)), p.id]
												: (split ?? party.map((x) => x.id)).filter(
														(id) => id !== p.id,
													),
										)
									}
								/>
								{p.nickname}
							</label>
						))}
					</fieldset>
				)}
			</details>
			{editing && (
				<button
					type="button"
					onClick={() => {
						if (history.state?.sheet) history.back();
						else closeForm();
					}}
				>
					고치기 취소
				</button>
			)}
			{error && <p role="alert">{error}</p>}
		</form>
	);
	return (
		<section className={quick ? "quick-expense" : "money-panel"}>
			<h2>{quick ? "지출 남기기" : "돈 · 예산과 일행 정산"}</h2>
			{!quick && (
				<section className="settlement-first">
					<h3>누가 누구에게 보내면 될까요?</h3>
					{settlement.breakdown.length > 0 && <p>{party[0]!.nickname}의 몫 ¥{settlement.shareTotals[party[0]!.id]!.toLocaleString()} · 일행별 몫은 정산 근거에서 확인해요.</p>}
					{settlement.rows.length ? (
						settlement.rows.map((row) => (
							<p key={`${row.from}:${row.to}`}>
								{name(row.from)} → {name(row.to)} · ¥
								{row.amountJpy.toLocaleString()}
							</p>
						))
					) : (
						<p>
							{trip.expenses.length
								? "정산할 엔화 금액이 없어요."
								: "지출을 기록하면 정산을 볼 수 있어요."}
						</p>
					)}
					{settlement.breakdown.length > 0 && (
						<details>
							<summary>정산 근거 보기</summary>
							<ul>{party.map((person) => <li key={person.id}>{person.nickname} · 낸 돈 ¥{settlement.paidTotals[person.id]!.toLocaleString()} · 몫 ¥{settlement.shareTotals[person.id]!.toLocaleString()} · 차액 ¥{settlement.balances[person.id]!.toLocaleString()}</li>)}</ul>
							<p>차액 합계 ¥{Object.values(settlement.balances).reduce((a, b) => a + b, 0).toLocaleString()} · 합계가 0이면 검산이 맞아요.</p>
							<ol>{settlement.breakdown.map((entry) => <li key={entry.id}>{entry.title} · ¥{entry.amount.toLocaleString()} · {name(entry.paidBy)}가 냄 · {entry.shares.map((share) => `${name(share.id)} ¥${share.amount.toLocaleString()}`).join(" · ")}</li>)}</ol>
						</details>
					)}
					{settlement.excluded > 0 && (
						<p>환율이 없는 원화 지출은 별도로 확인해요.</p>
					)}
				</section>
			)}
			<p>
				{quick
					? `오늘 남은 ¥${Math.max(0, trip.dailyBudgetJpy * trip.partySize - todaySpent).toLocaleString()}`
					: `여행 예산 ¥${(trip.dailyBudgetJpy * trip.partySize * trip.days.length).toLocaleString()} · 지출 ¥${sum.toLocaleString()}`}
				{unconverted > 0 &&
					` · 별도 원화 ${unconverted.toLocaleString()}원 (환율 없음)`}
			</p>
			{quick ? (
				expenseForm
			) : (
				<>
					<button
						type="button"
						className="button primary"
						onClick={() => {
							setEditing(null);
							setCurrency("JPY");
							setPaidBy("");
							setSplit(null);
							setError("");
							dirty.current = false;
							setShowForm(true);
						}}
					>
						지출 남기기
					</button>
					{showForm && (
						<BottomSheet
							title={editing ? "지출 고치기" : "지출 남기기"}
							onClose={closeForm}
						>
							{expenseForm}
						</BottomSheet>
					)}
				</>
			)}

			{!quick && <ExpenseImport trip={trip} persist={persist} />}
			<p role="alert">{error}</p>
			<p role="status">{notice}</p>
			{!quick && (
				<>
					<div className="expense-list">
						{trip.expenses.length ? (
							trip.expenses.map((e) => (
								<article key={e.id}>
									<span>
										{e.occurredOn} · {categories[e.category]}
									</span>
									<strong>{e.title}</strong>
									<b>
										{e.currency === "KRW"
											? `${e.amountKrw?.toLocaleString()}원`
											: `¥${e.amountJpy.toLocaleString()}`}
									</b>
									<button
										type="button"
										onClick={() => {
											setEditing(e);
											dirty.current = false;
											setShowForm(true);
											setCurrency(e.currency ?? "JPY");
											setPaidBy(e.paidBy ?? party[0]!.id);
											setSplit(e.splitWith ?? null);
										}}
									>
										이 지출 고치기
									</button>
									<button
										type="button"
										onClick={() =>
											void persist((t) => {
												t.expenses = t.expenses.filter((x) => x.id !== e.id);
											})
										}
									>
										지출 삭제
									</button>
								</article>
							))
						) : (
							<p>지출을 남기면 여기서 바로 나눠 드려요.</p>
						)}
					</div>
					{trip.expenses.length > 0 && (
						<>
							<h3>분류·날짜별 지출</h3>
							<table>
								<caption>엔화 합계 · 환율 없는 원화는 별도</caption>
								<thead>
									<tr>
										<th>분류·날짜</th>
										<th>합계 · 엔</th>
									</tr>
								</thead>
								<tbody>
									{[
										...Object.keys(categories),
										...trip.days.map((d) => d.date),
									].map((key) => {
										const value = trip.expenses
											.filter((e) => e.category === key || e.occurredOn === key)
											.reduce(
												(n, e) => n + (expenseJpy(e, trip.krwPer100Jpy) ?? 0),
												0,
											);
										return (
											<tr key={key}>
												<th>
													{categories[key as keyof typeof categories] ?? key}
												</th>
												<td>
													<meter value={value} max={Math.max(1, sum)} />
													{value.toLocaleString()}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
							<button
								type="button"
								className="button secondary"
								onClick={() =>
									downloadTripFile(
										"일본골목-지출.csv",
										expensesCsv(trip),
										"text/csv;charset=utf-8",
									)
								}
							>
								엑셀로 받기
							</button>
							{(isAppsInTossChannel() ||
								/KAKAOTALK/i.test(navigator.userAgent)) && (
								<CopyText text={expensesCsv(trip)} label="지출 글로 복사" />
							)}
						</>
					)}
					<h3>누가 누구에게 보내면 될까요?</h3>
					{settlement.breakdown.length > 0 && <p>{party[0]!.nickname}의 몫 ¥{settlement.shareTotals[party[0]!.id]!.toLocaleString()} · 일행별 몫은 정산 근거에서 확인해요.</p>}
					<p>
						송금은 사용하는 앱에서 직접 보내 주세요. 일본골목은 송금을 연결하지
						않아요. 환율은 자동 시세가 아니에요.
					</p>
					{!trip.krwPer100Jpy && unconverted > 0 && (
						<>
							<p>
								원화는 따로 나눠요. 여행 정보 고치기에서 환율을 적으면 엔화로
								함께 계산해요.
							</p>
							{settleExpenses(trip, "KRW").rows.map((r, i) => (
								<p key={`${r.from}:${r.to}`}>
									{name(r.from)} → {name(r.to)} · {r.amountJpy.toLocaleString()}
									원
								</p>
							))}
						</>
					)}
					{settlement.rows.map((row, i) => {
						const done = trip.settlements?.some(
							(x) =>
								x.from === row.from &&
								x.to === row.to &&
								x.amountJpy === row.amountJpy,
						);
						return (
							<article key={`${row.from}:${row.to}`}>
								<p>
									{name(row.from)} → {name(row.to)} · ¥
									{row.amountJpy.toLocaleString()}
									{trip.krwPer100Jpy &&
										` · 약 ${jpyToKrw(row.amountJpy, trip.krwPer100Jpy)?.toLocaleString()}원`}
								</p>
								<CopyText text={String(row.amountJpy)} label="금액 복사" />
								<button
									type="button"
									aria-pressed={Boolean(done)}
									onClick={() =>
										void persist((t) => {
											const records = t.settlements ?? [];
											t.settlements = done
												? records.filter(
														(x) =>
															!(
																x.from === row.from &&
																x.to === row.to &&
																x.amountJpy === row.amountJpy
															),
													)
												: [
														...records.filter(
															(x) => !(x.from === row.from && x.to === row.to),
														),
														{ ...row, doneAt: new Date().toISOString() },
													];
										})
									}
								>
									{done ? "보냈어요 ✓" : "보냈어요"}
								</button>
							</article>
						);
					})}
					{trip.settlements?.some(
						(x) =>
							!settlement.rows.some(
								(r) =>
									r.from === x.from &&
									r.to === x.to &&
									r.amountJpy === x.amountJpy,
							),
					) && <p role="status">정산이 바뀌었어요. 다시 확인해 주세요.</p>}
					{settlement.rows.length > 0 &&
						settlement.rows.every((r) =>
							trip.settlements?.some(
								(x) =>
									x.from === r.from &&
									x.to === r.to &&
									x.amountJpy === r.amountJpy,
							),
						) && <p role="status">정산 끝</p>}
					{settlement.rows.length > 0 && (
						<button
							type="button"
							className="button secondary"
							onClick={() => setSend(true)}
						>
							정산 보내기
						</button>
					)}
					{send && (
						<section>
							<p>받는 사람이 별명과 금액을 볼 수 있어요.</p>
							<SendPayload
								payload={{
									kind: "payment",
									rows: settlement.rows.map((r) => ({
										...r,
										from: name(r.from),
										to: name(r.to),
									})),
								}}
								text={settlement.rows
									.map((r) => `${name(r.from)} → ${name(r.to)} ¥${r.amountJpy}`)
									.join("\n")}
							/>
						</section>
					)}
				</>
			)}
		</section>
	);
}
